import { useState, type FormEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { FormDialog } from '../../chrome/form';
import { Hint } from '../../chrome/hint';
import type { ApiFailure } from '../../lib/http/errors';
import { ApiFailureError } from '../../lib/http/errors';
import { Field, FieldDescription, FieldLabel } from '../../ui/field';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { opsKeys, useAddAdmin } from '../api/queries';
import { t } from './messages';

export function GrantAdminDialog(props: {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}): ReactNode {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = props.open !== undefined ? props.open : internalOpen;

  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const addAdmin = useAddAdmin();
  const queryClient = useQueryClient();

  function setOpen(next: boolean): void {
    if (props.open === undefined) {
      setInternalOpen(next);
    }
    props.onOpenChange?.(next);
    if (!next) {
      setFailure(null);
      setEmail('');
      setNote('');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setFailure(null);
    try {
      await addAdmin.mutateAsync({ email, note: note || undefined });
      setOpen(false);
      await queryClient.invalidateQueries({ queryKey: opsKeys.admins() });
    } catch (cause) {
      if (cause instanceof ApiFailureError) {
        if (cause.failure.kind === 'not-found') {
          setFailure({ kind: 'invalid', field: null, message: t('admins.grant.notSignedIn') });
        } else if (cause.failure.kind === 'conflict') {
          setFailure({ kind: 'invalid', field: null, message: t('admins.grant.conflict') });
        } else {
          setFailure(cause.failure);
        }
      } else {
        setFailure({ kind: 'server', status: 500 });
      }
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={setOpen}
      title={t('admins.grant.title')}
      submitLabel={t('admins.grant.open')}
      pending={addAdmin.isPending}
      failure={failure}
      onSubmit={(event) => void handleSubmit(event)}
      trigger={props.trigger}
    >
      <Field>
        {/* Hint sits beside FieldLabel, not inside it -- FieldLabel renders a
            real <label>, and a nested button's own aria-label would fold
            into the input's computed accessible name. */}
        <div className="flex items-center gap-2">
          <FieldLabel htmlFor="grant-admin-email">{t('admins.grant.email.label')}</FieldLabel>
          <Hint label={t('admins.grant.email.label')}>{t('admins.grant.email.hint')}</Hint>
        </div>
        <Input
          id="grant-admin-email"
          name="email"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <FieldDescription>{t('admins.grant.email.hint')}</FieldDescription>
      </Field>
      <Field>
        <div className="flex items-center gap-2">
          <FieldLabel htmlFor="grant-admin-note">{t('admins.grant.note.label')}</FieldLabel>
          <Hint label={t('admins.grant.note.label')}>{t('admins.grant.note.hint')}</Hint>
        </div>
        <Textarea
          id="grant-admin-note"
          name="note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <FieldDescription>{t('admins.grant.note.hint')}</FieldDescription>
      </Field>
    </FormDialog>
  );
}
